import React from 'react';
import { WifiOff, RefreshCw, CheckCircle } from 'lucide-react';
import { formatTime24 } from '../utils/formatTime';

/**
 * RENTOS Network Status Banner
 * Shows offline warning and connection restored notification.
 */
export const NetworkBanner = ({ isOnline, wasOffline, onResync, isSyncing }) => {
  // Offline banner
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#EF4444] text-white px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm font-medium">
          <WifiOff className="w-4 h-4" />
          <span>Интернет холболт тасарсан байна</span>
        </div>
      </div>
    );
  }

  // Connection restored banner (auto-dismiss)
  if (wasOffline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#10B981] text-white px-4 py-2 animate-pulse">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm font-medium">
          <CheckCircle className="w-4 h-4" />
          <span>Холболт сэргэлээ</span>
          {onResync && (
            <button
              onClick={onResync}
              disabled={isSyncing}
              className="ml-2 px-2 py-1 bg-white/20 rounded text-xs flex items-center gap-1 hover:bg-white/30"
            >
              <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
              Sync
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

/**
 * RENTOS Manual Sync Button
 * Visible for Admin/Manager to force data refresh.
 */
export const SyncButton = ({ onSync, isSyncing, lastSyncTime, className = '' }) => {
  return (
    <button
      onClick={onSync}
      disabled={isSyncing}
      className={`p-2 rounded-lg bg-[#1F2937] hover:bg-[#374151] text-[#9CA3AF] hover:text-white transition-all disabled:opacity-50 ${className}`}
      title={lastSyncTime ? `Сүүлд: ${formatTime24(lastSyncTime)}` : 'Дата шинэчлэх'}
    >
      <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
    </button>
  );
};

const isLoopback = typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

const syncSummary = (s) => {
  if (!s || !s.sync_enabled) return null;
  if (s.error_count > 0) return { tone: '#b91c1c', text: `${s.error_count} sync events need attention` };
  if (s.cloud_reachable === false) return { tone: '#b45309', text: `Cloud unavailable · Pending: ${s.pending_outbox}` };
  if (s.pending_outbox + (s.inflight || 0) > 0) return { tone: '#1d4ed8', text: `Syncing · ${s.pending_outbox + (s.inflight || 0)} pending` };
  return { tone: '#047857', text: 'Cloud sync: Up to date' };
};

/**
 * Cloud sync status pill — Local POS (loopback) only. Summary for cashiers; no technical details.
 */
export const CloudSyncStatusBadge = () => {
  const [status, setStatus] = React.useState(null);

  React.useEffect(() => {
    if (!isLoopback) return undefined;
    let active = true;
    const load = () => fetch('/api/sync/status')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active) setStatus(d); })
      .catch(() => { if (active) setStatus((prev) => (prev ? { ...prev, cloud_reachable: false } : prev)); });
    load();
    const timer = setInterval(load, 30000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  const summary = syncSummary(status);
  if (!summary) return null;
  return (
    <div
      data-testid="cloud-sync-status"
      className="fixed top-6 left-1/2 -translate-x-1/2 z-[9998] px-3 py-0.5 rounded-b-md text-[11px] font-semibold select-none pointer-events-none"
      style={{ background: summary.tone, color: '#fff' }}
    >
      {status.shadow_mode ? 'SHADOW · ' : ''}{summary.text}
    </div>
  );
};

export default NetworkBanner;
